import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { getUserAppDraft, type DraftDetailsUpdate, type UserAppDraft } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import useSnackbar from '@hooks/useSnackbar';
import { isDraftConflict } from '../appBuilderErrors';
import { appBuilderKeys, useAppBuilderActions, useCoverPresets } from '../useAppBuilder';
import { useAppThumbnail } from './useAppThumbnail';

type CoverUpdate = Omit<DraftDetailsUpdate, 'expectedLockVersion'>;

export const useCoverField = (draft: UserAppDraft, onReload: () => void) => {
  const intl = useIntl();
  const { showError } = useSnackbar();
  const queryClient = useQueryClient();
  const presets = useCoverPresets();
  const thumbnail = useAppThumbnail(draft.appKey);
  const { updateDraft } = useAppBuilderActions();
  const saving = useIsMutating({ mutationKey: appBuilderKeys.updateDraft }) > 0;
  const [direction, setDirection] = useState('');
  const [replacing, setReplacing] = useState<CoverUpdate | null>(null);
  const presentation = draft.manifest.presentation;
  const hasThumbnail = Boolean(presentation?.thumbnail);
  const { appKey } = draft;

  const failed = (fallback: string) => (err: unknown) => {
    if (isDraftConflict(err)) onReload();
    showError(axiosErrorMessage(err, fallback));
  };

  // The assistant and the Details form save the same draft, so a lost race is retried once on the version it lost to.
  const saveOnce = async (update: CoverUpdate) => {
    try {
      await updateDraft({ appKey, update: { expectedLockVersion: draft.lockVersion, ...update } });
    } catch (err) {
      if (!isDraftConflict(err)) throw err;
      const fresh = await queryClient.fetchQuery({
        queryKey: appBuilderKeys.draft(appKey),
        queryFn: () => getUserAppDraft(appKey),
        staleTime: 0,
      });
      await updateDraft({ appKey, update: { expectedLockVersion: fresh.lockVersion, ...update } });
    }
  };

  const save = (update: CoverUpdate) =>
    saveOnce(update).catch(
      failed(intl.formatMessage({ id: 'apps.builder.details.error', defaultMessage: 'Your changes could not be saved.' })),
    );

  // A generated thumbnail was paid for and no version restores it, so dropping it is confirmed first.
  const replaceThumbnail = (update: CoverUpdate) => (hasThumbnail ? setReplacing(update) : save(update));

  const generate = () => {
    thumbnail
      .generate(direction)
      .then(() => setDirection(''))
      .catch(
        failed(
          intl.formatMessage({
            id: 'apps.builder.thumbnail.error',
            defaultMessage: 'The thumbnail could not be generated. Try again.',
          }),
        ),
      );
  };

  return {
    presets: presets.data ?? [],
    selectedPreset: presentation?.coverPreset ?? null,
    hasThumbnail,
    thumbnail,
    direction,
    setDirection,
    saving,
    generate,
    pickPreset: (coverPreset: string) =>
      hasThumbnail ? replaceThumbnail({ coverPreset, removeThumbnail: true }) : save({ coverPreset }),
    replacing: replacing !== null,
    confirmReplace: () => (replacing ? save(replacing) : undefined),
    cancelReplace: () => setReplacing(null),
  };
};
